// Visibility editor — task 104f D5.
//
// Opened from the row kebab on `/settings/access` → "Edit visibility".
// Admin-only. Radio group for the five scope values; conditional
// multi-select below the radio group for `bu_list` (Business Units) and
// `employee_list` (Employees). Save calls `PUT /memberships/:id/visibility`
// via the `useUpdateVisibility` mutation hook in `useOrgAccess`.
//
// Owner / admin rows render a static note alongside the editor so the
// admin knows the stored scope is cosmetic (resolver returns `'all'`).

import { useState, type FormEvent } from 'react';
import { SlidePanel } from '@/components/composites/SlidePanel';
import { Button } from '@/components/primitives/Button';
import { useBusinessUnits } from '@/hooks/useBusinessUnits';
import { useEmployees } from '@/hooks/useEmployees';
import { useUpdateVisibility, type KanjoyUser, type VisibilityScope } from '@/hooks/useOrgAccess';
import styles from './VisibilityEditorModal.module.css';

export interface VisibilityEditorModalProps {
  open: boolean;
  onClose: () => void;
  user: KanjoyUser | null;
}

// Re-seed the editor whenever the admin opens it for a different member.
// Remounting is cheaper than an effect that calls setState, and lint
// blocks the effect pattern. Wrap the real editor and pass the key from
// the parent component so `user?.id` changes force a fresh mount.
export function VisibilityEditorModal(props: VisibilityEditorModalProps) {
  return <VisibilityEditorModalInner key={props.user?.id ?? 'none'} {...props} />;
}

interface ScopeOption {
  value: VisibilityScope;
  label: string;
  description: string;
}

const SCOPE_OPTIONS: readonly ScopeOption[] = [
  { value: 'self', label: 'Self only', description: "Only the member's own activity." },
  { value: 'all', label: 'All activity', description: 'Every Employee in the organization.' },
  { value: 'own_bu', label: 'Own BU', description: "The member's Business Unit." },
  { value: 'bu_list', label: 'BU list', description: 'A specific list of Business Units.' },
  {
    value: 'employee_list',
    label: 'Employee list',
    description: 'A specific list of Employees.',
  },
];

function VisibilityEditorModalInner({ open, onClose, user }: VisibilityEditorModalProps) {
  // Initial state is seeded from the user prop — the outer wrapper
  // remounts this component whenever `user.id` changes, so the initial
  // seed is always correct without an effect.
  const [scope, setScope] = useState<VisibilityScope>(user?.visibilityScope ?? 'self');
  // TODO(104f-followup): fetch existing business-unit / employee id lists
  // via GET /visibility so re-editing preserves them. For the first drop
  // admins re-pick the lists on every edit; the PUT semantic is a full
  // replace so this is correct, just less convenient.
  const [buIds, setBuIds] = useState<string[]>([]);
  const [empIds, setEmpIds] = useState<string[]>([]);
  const update = useUpdateVisibility();

  const { data: businessUnits } = useBusinessUnits();
  const { data: employees } = useEmployees();

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!user) return;
    update.mutate(
      {
        membershipId: user.id,
        scope,
        businessUnitIds: scope === 'bu_list' ? buIds : undefined,
        employeeIds: scope === 'employee_list' ? empIds : undefined,
      },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  };

  const isOverridden = user?.role === 'owner' || user?.role === 'admin';

  return (
    <SlidePanel
      open={open}
      onClose={onClose}
      title="Edit visibility"
      subtitle={
        user
          ? `Change what ${user.name} can see on reporting and timesheet pages.`
          : 'Change visibility scope.'
      }
    >
      <form onSubmit={handleSubmit} className={styles.form}>
        {isOverridden && user && (
          <p className={styles.overrideNote} role="status">
            {user.role === 'owner' ? 'Owner' : 'Admin'} roles always see all activity — the stored
            scope is only applied if this user is later demoted to member.
          </p>
        )}
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Scope</legend>
          {SCOPE_OPTIONS.map((opt) => {
            const rid = `visibility-scope-${opt.value}`;
            return (
              <div key={opt.value} className={styles.radioRow}>
                <input
                  id={rid}
                  type="radio"
                  name="visibility-scope"
                  value={opt.value}
                  checked={scope === opt.value}
                  onChange={() => setScope(opt.value)}
                />
                <label htmlFor={rid} className={styles.radioBody}>
                  <span className={styles.radioLabel}>{opt.label}</span>
                  <span className={styles.radioDesc}>{opt.description}</span>
                </label>
              </div>
            );
          })}
        </fieldset>

        {scope === 'bu_list' && (
          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>Business Units</legend>
            {businessUnits.length === 0 ? (
              <p className={styles.muted}>No Business Units defined in this org yet.</p>
            ) : (
              businessUnits.map((bu) => {
                const cid = `visibility-bu-${bu.id}`;
                return (
                  <div key={bu.id} className={styles.checkRow}>
                    <input
                      id={cid}
                      type="checkbox"
                      checked={buIds.includes(bu.id)}
                      onChange={(e) => {
                        setBuIds((prev) =>
                          e.target.checked ? [...prev, bu.id] : prev.filter((id) => id !== bu.id),
                        );
                      }}
                    />
                    <label htmlFor={cid}>{bu.name}</label>
                  </div>
                );
              })
            )}
          </fieldset>
        )}

        {scope === 'employee_list' && (
          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>Employees</legend>
            {employees.length === 0 ? (
              <p className={styles.muted}>No Employees defined in this org yet.</p>
            ) : (
              <div className={styles.scrollList}>
                {employees.map((emp) => {
                  const cid = `visibility-emp-${emp.id}`;
                  return (
                    <div key={emp.id} className={styles.checkRow}>
                      <input
                        id={cid}
                        type="checkbox"
                        checked={empIds.includes(emp.id)}
                        onChange={(e) => {
                          setEmpIds((prev) =>
                            e.target.checked
                              ? [...prev, emp.id]
                              : prev.filter((id) => id !== emp.id),
                          );
                        }}
                      />
                      <label htmlFor={cid}>{emp.name}</label>
                    </div>
                  );
                })}
              </div>
            )}
          </fieldset>
        )}

        {update.error ? (
          <p className={styles.error} role="alert">
            {(update.error as Error).message}
          </p>
        ) : null}

        <div className={styles.formActions}>
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </SlidePanel>
  );
}
